import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ButtonType } from '@src/components/Button'
import { AlertModal } from '@src/components/Modal/AlertModal'
import { useBasketItems } from '@src/hooks/useBasketItems/useBasketItems'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { BasketItemWithPrices } from '@src/hooks/useTotals/useBasketTotals'
import { useBasketTotalsContext } from '@src/hooks/useTotals/BasketTotalsContext'

import { BasketItem as BasketItemComponent } from './BasketItem'
import { Container } from './BasketItems.styles'

export const BasketItems: React.FC = () => {
  const marketplace = useMarketplace()
  const basketItems = useBasketItems()
  const { basketTotals } = useBasketTotalsContext()
  // modal needs a separate state cause the item get removed before the modal is closed
  const [showModal, setShowModal] = useState(false)

  const [modalData, setModalData] = useState<null | {
    title: string
    subtitle: string
    onModalCloseAfterConfirmAction: () => void
  }>(null)

  useEffect(() => {
    if (modalData) {
      setShowModal(true)
    }
  }, [modalData])

  const { t } = useTranslation('checkout')

  const basketTotalsData = basketTotals.data

  const allowSingleItemOrderNotes = !!marketplace.allowSingleItemOrderNotes

  // function to update the quantity of a basket item
  const curriedUpdateBasketItemQuantity =
    (basketItemWithPrices: BasketItemWithPrices) => (newQuantity: number) => {
      // if it's an add-on item or the quantity is being increased, we don't need to do anything special
      if (
        basketItemWithPrices.isAddOnItem ||
        newQuantity >= basketItemWithPrices.basketItem.quantity
      ) {
        return basketItems.updateQuantity({
          basketItemId: basketItemWithPrices.basketItem.id,
          quantity: newQuantity,
        })
      }

      // if the item is not an add-on item, we need to check if the modal should be shown. Which is either:
      // * the quantity is being set to 0
      // * the quantity is being set to a value that would take the total below the threshold (if there is one)
      const basketTotalReductionAmount =
        basketItemWithPrices.totalPriceForOneItem *
        (basketItemWithPrices.basketItem.quantity - newQuantity)
      const isAddOnItemRemovalRequired =
        typeof basketTotalsData.checkoutBasketTotals
          ?.amountOverAddOnThreshold === 'number' &&
        basketTotalReductionAmount >
          basketTotalsData.checkoutBasketTotals.amountOverAddOnThreshold

      if (newQuantity === 0 || isAddOnItemRemovalRequired) {
        setModalData({
          title: t(
            isAddOnItemRemovalRequired
              ? 'remove_item_and_add_on_items'
              : 'remove_item'
          ),
          subtitle: isAddOnItemRemovalRequired
            ? t('remove_item_and_add_on_items_desc')
            : t('remove_item_desc', {
                itemName: basketItemWithPrices.basketItem.name,
              }),
          onModalCloseAfterConfirmAction: () => {
            basketItems.updateQuantities({
              basketItemUpdates: [
                {
                  basketItemId: basketItemWithPrices.basketItem.id,
                  quantity: newQuantity,
                },
                ...(isAddOnItemRemovalRequired
                  ? basketTotalsData.basketItemsWithPrices
                      .filter(item => item.isAddOnItem)
                      .map(item => ({
                        basketItemId: item.basketItem.id,
                        quantity: 0,
                      }))
                  : []),
              ],
            })
          },
        })
        return
      }

      // quantity is being reduced but not to 0, and not below the threshold
      return basketItems.updateQuantity({
        basketItemId: basketItemWithPrices.basketItem.id,
        quantity: newQuantity,
      })
    }

  return (
    <>
      <AlertModal
        isOpen={showModal}
        title={modalData ? modalData.title : ''}
        subTitle={modalData ? modalData.subtitle : ''}
        action={{
          text: t('confirm'),
          intent: ButtonType.DANGER,
          onClick: () => {
            if (modalData) {
              setShowModal(false)
              setTimeout(() => {
                modalData.onModalCloseAfterConfirmAction()
              }, 1000)
            }
          },
        }}
        cancel={{
          text: t('cancel'),
          intent: ButtonType.SECONDARY,
          onClick: () => {
            setShowModal(false)
            setTimeout(() => {
              setModalData(null)
            }, 1000)
          },
        }}
      />
      <Container>
        {basketTotalsData.basketItemsWithPrices.map(basketItemWithPrices => (
          <BasketItemComponent
            key={basketItemWithPrices.basketItem.id}
            basketItemWithPrices={basketItemWithPrices}
            allowSingleItemOrderNotes={allowSingleItemOrderNotes}
            updateQuantity={curriedUpdateBasketItemQuantity(
              basketItemWithPrices
            )}
          />
        ))}
      </Container>
    </>
  )
}
